// Full-screen menu sheet for the visualizer. One container holds every
// section (Source / Shape / Color / Audio). Closed by default with a small
// hamburger button in the bottom-right corner. Tap the button to open, X
// in the sheet header to close.
//
// On desktop the sheet is centered with max-width 640px and a backdrop blur
// behind it. On mobile it's edge-to-edge.
//
// Open/close state is persisted to localStorage so a reload keeps the same
// posture as the last interaction.

const LS_OPEN_KEY = 'dp-menu-sheet-open';

export class MenuSheet {
  constructor({ mountEl = document.body, title = 'Controls', initialOpen } = {}) {
    this.mountEl = mountEl;
    this.title = title;
    if (initialOpen === undefined) {
      const stored = (typeof localStorage !== 'undefined') ? localStorage.getItem(LS_OPEN_KEY) : null;
      this._open = stored === '1';
    } else {
      this._open = !!initialOpen;
    }
    this._listeners = { open: [], close: [] };
    this._build();
  }

  _build() {
    this._injectStyle();

    // Hamburger toggle button, persistent in the bottom-right corner
    this.toggleBtn = document.createElement('button');
    this.toggleBtn.className = 'dp-menu-toggle';
    this.toggleBtn.setAttribute('aria-label', 'Open menu');
    this.toggleBtn.type = 'button';
    this.toggleBtn.textContent = '☰'; // ☰

    // Backdrop covers the canvas when the sheet is open
    this.backdrop = document.createElement('div');
    this.backdrop.className = 'dp-menu-backdrop';

    // Sheet container — the box itself
    this.sheet = document.createElement('div');
    this.sheet.className = 'dp-menu-sheet';
    this.sheet.setAttribute('role', 'dialog');
    this.sheet.setAttribute('aria-label', this.title);

    // Header with title and close X
    this.header = document.createElement('div');
    this.header.className = 'dp-menu-header';

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'dp-menu-title';
    this.titleEl.textContent = this.title;

    this.closeBtn = document.createElement('button');
    this.closeBtn.className = 'dp-menu-close';
    this.closeBtn.setAttribute('aria-label', 'Close menu');
    this.closeBtn.type = 'button';
    this.closeBtn.textContent = '×'; // ×

    this.header.appendChild(this.titleEl);
    this.header.appendChild(this.closeBtn);

    // Scrollable body where sections live
    this.body = document.createElement('div');
    this.body.className = 'dp-menu-body';

    this.sheet.appendChild(this.header);
    this.sheet.appendChild(this.body);

    this._onToggle = (e) => { e?.stopPropagation?.(); this.toggle(); };
    this._onClose = (e) => { e?.stopPropagation?.(); this.close(); };
    this._onBackdropClick = (e) => {
      // Click anywhere outside the sheet (i.e. on backdrop) closes
      if (e.target === this.backdrop) this.close();
    };
    this._onKey = (e) => { if (e.key === 'Escape' && this._open) this.close(); };

    this.toggleBtn.addEventListener('click', this._onToggle);
    this.closeBtn.addEventListener('click', this._onClose);
    this.backdrop.addEventListener('click', this._onBackdropClick);
    window.addEventListener('keydown', this._onKey);

    this._applyOpenState();
  }

  _injectStyle() {
    if (document.getElementById('dp-menu-sheet-style')) return;
    const style = document.createElement('style');
    style.id = 'dp-menu-sheet-style';
    style.textContent = `
.dp-menu-toggle {
  position: fixed;
  right: 16px;
  bottom: 16px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.18);
  background: rgba(20,22,28,0.78);
  color: #e8ecf3;
  font-size: 20px;
  line-height: 48px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.45);
  z-index: 30;
  transition: transform 120ms ease, opacity 120ms ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.dp-menu-toggle:hover { transform: scale(1.05); }
.dp-menu-toggle.hidden { opacity: 0; pointer-events: none; transform: scale(0.9); }

.dp-menu-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: 40;
  opacity: 0;
  pointer-events: none;
  transition: opacity 160ms ease;
}
.dp-menu-backdrop.open { opacity: 1; pointer-events: auto; }

.dp-menu-sheet {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  max-width: 480px;
  background: rgba(16,18,24,0.96);
  border-left: 1px solid rgba(255,255,255,0.08);
  box-shadow: -8px 0 32px rgba(0,0,0,0.55);
  display: flex;
  flex-direction: column;
  z-index: 50;
  transform: translateX(100%);
  transition: transform 200ms ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #e8ecf3;
}
.dp-menu-sheet.open { transform: translateX(0); }

@media (max-width: 480px) {
  .dp-menu-sheet { max-width: 100%; border-left: none; }
}

.dp-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  flex: 0 0 auto;
}
.dp-menu-title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #c8d0db;
}
.dp-menu-close {
  background: transparent;
  border: none;
  color: #e8ecf3;
  font-size: 28px;
  line-height: 1;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: 8px;
  padding: 0;
}
.dp-menu-close:hover { background: rgba(255,255,255,0.08); }
.dp-menu-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 12px 14px 24px;
  overscroll-behavior: contain;
}
`;
    document.head.appendChild(style);
  }

  mount() {
    this.mountEl.appendChild(this.toggleBtn);
    this.mountEl.appendChild(this.backdrop);
    this.mountEl.appendChild(this.sheet);
  }

  open() {
    if (this._open) return;
    this._open = true;
    this._persist();
    this._applyOpenState();
    this._emit('open');
  }

  close() {
    if (!this._open) return;
    this._open = false;
    this._persist();
    this._applyOpenState();
    this._emit('close');
  }

  toggle() {
    if (this._open) this.close(); else this.open();
  }

  isOpen() {
    return this._open;
  }

  appendSection(node) {
    this.body.appendChild(node);
  }

  addEventListener(type, fn) {
    if (!this._listeners[type]) this._listeners[type] = [];
    this._listeners[type].push(fn);
  }

  removeEventListener(type, fn) {
    if (!this._listeners[type]) return;
    this._listeners[type] = this._listeners[type].filter((f) => f !== fn);
  }

  destroy() {
    this.toggleBtn.removeEventListener('click', this._onToggle);
    this.closeBtn.removeEventListener('click', this._onClose);
    this.backdrop.removeEventListener('click', this._onBackdropClick);
    window.removeEventListener('keydown', this._onKey);
    this.toggleBtn.remove();
    this.backdrop.remove();
    this.sheet.remove();
    this._listeners = { open: [], close: [] };
  }

  _emit(type) {
    (this._listeners[type] || []).forEach((fn) => {
      try { fn(); } catch (err) { console.error('[menu-sheet] listener threw', err); }
    });
  }

  _persist() {
    if (typeof localStorage === 'undefined') return;
    try { localStorage.setItem(LS_OPEN_KEY, this._open ? '1' : '0'); } catch (_) { /* private mode */ }
  }

  _applyOpenState() {
    if (this._open) {
      this.sheet.classList.add('open');
      this.backdrop.classList.add('open');
      this.toggleBtn.classList.add('hidden');
    } else {
      this.sheet.classList.remove('open');
      this.backdrop.classList.remove('open');
      this.toggleBtn.classList.remove('hidden');
    }
  }
}

// Helper: a collapsible section card for use inside the sheet body. Has its
// own header (tap to expand/collapse), persists state to localStorage. The
// caller passes a body element (already populated) — we just wrap it.
export function collapsibleSection({ key, title, bodyEl, defaultOpen = true }) {
  const wrap = document.createElement('section');
  wrap.className = 'dp-section';

  const head = document.createElement('button');
  head.className = 'dp-section-head';
  head.type = 'button';

  const chev = document.createElement('span');
  chev.className = 'dp-section-chevron';
  chev.textContent = '▾'; // ▾

  const label = document.createElement('span');
  label.className = 'dp-section-title';
  label.textContent = title;

  head.appendChild(chev);
  head.appendChild(label);

  const wrapBody = document.createElement('div');
  wrapBody.className = 'dp-section-body';
  wrapBody.appendChild(bodyEl);

  wrap.appendChild(head);
  wrap.appendChild(wrapBody);

  let open = defaultOpen;
  const storageKey = key ? `dp-section-${key}` : null;
  if (storageKey && typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(storageKey);
    if (stored === '0') open = false;
    if (stored === '1') open = true;
  }

  const apply = () => {
    wrap.classList.toggle('collapsed', !open);
    chev.style.transform = open ? 'rotate(0deg)' : 'rotate(-90deg)';
  };
  apply();

  head.addEventListener('click', () => {
    open = !open;
    apply();
    if (storageKey && typeof localStorage !== 'undefined') {
      try { localStorage.setItem(storageKey, open ? '1' : '0'); } catch (_) { /* */ }
    }
  });

  return wrap;
}

// Inject the section styles once. Called from main.js side or implicitly
// when the first section is built.
let sectionStyleInjected = false;
export function injectSectionStyle() {
  if (sectionStyleInjected || typeof document === 'undefined') return;
  if (document.getElementById('dp-menu-section-style')) {
    sectionStyleInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'dp-menu-section-style';
  style.textContent = `
.dp-section {
  background: rgba(255,255,255,0.025);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px;
  margin: 10px 0;
  overflow: hidden;
}
.dp-section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  background: transparent;
  border: none;
  color: #e8ecf3;
  text-align: left;
  cursor: pointer;
  font: 600 13px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  letter-spacing: 0.03em;
  text-transform: uppercase;
}
.dp-section-head:hover { background: rgba(255,255,255,0.04); }
.dp-section-chevron {
  display: inline-block;
  width: 12px;
  font-size: 11px;
  color: #8a96a8;
  transition: transform 140ms ease;
}
.dp-section-title { flex: 1 1 auto; }
.dp-section-body {
  padding: 8px 14px 14px;
  border-top: 1px solid rgba(255,255,255,0.05);
}
.dp-section.collapsed .dp-section-body { display: none; }
`;
  document.head.appendChild(style);
  sectionStyleInjected = true;
}
